import { useContext, useEffect, useRef, useState } from 'react';

import { EditOutlined, UserOutlined } from '@ant-design/icons';
// import { useCollectionStore } from '@store';
import {
	Avatar,
	Button,
	Flex,
	Form,
	Input,
	Modal,
	Select,
	Spin,
	Tooltip,
	Typography,
	message,
} from 'antd';
import type { SelectProps } from 'antd';
import { useForm } from 'antd/es/form/Form';

import {
	useAddCreatorToCreatorsCollection,
	useAddCreatorToMonitor,
	useGetCreatorByUrl,
	useGetCreatorsTags,
} from '@/api/monitor_collections';
import prepareImageSrc from '@/lib/helpers/prepareImageSrc';
import { validateCreatorUrl } from '@/lib/helpers/validateCreatorUrl';
import Platforms from '@/pages/app/dashboard/creators/platforms';
import { CollectionContext } from '@/providers/CollectionsContextProvider';

import placeholderImage from '../../lib/assets/placeholder.jpg';

type TProps = {
	isOpen: boolean;
	setOpen: any;
	type?: 'monitors' | 'creators';
};

export default function AddCreatorModal({
	isOpen = false,
	setOpen,
	type = 'monitors',
}: TProps) {
	const selectRef = useRef<any>(null);

	const [form] = useForm();
	// const [loading, setLoading] = useState(false);
	const { modalData } = useContext(CollectionContext);
	const [tags, setTags] = useState<SelectProps['options']>([]);
	const [inputValue, setInputValue] = useState('');
	const [searchUrl, setSearchUrl] = useState('');
	const [creator, setCreator] = useState<any>(null);

	const { mutate: addCreatorToMonitor, isLoading: isAddToMonitorLoading } =
		useAddCreatorToMonitor();

	const {
		mutate: addCreatorToCollection,
		isLoading: isAddToCollectionLoading,
	} = useAddCreatorToCreatorsCollection(modalData?.id);

	const {
		data: searchCreatorData,
		isLoading: isSearchCreatorLoading,
		isFetching: isSearchCreatorFetching,
		isError: isSearchCreatorError,
		isSuccess: isSearchCreatorSuccess,
		error: searchError,
	} = useGetCreatorByUrl(searchUrl);

	const { data: tagsData, isFetching: isTagsFetching } = useGetCreatorsTags({
		enabled: type === 'creators',
	});

	useEffect(() => {
		if (searchUrl && isSearchCreatorSuccess) {
			const creatorData = searchCreatorData?.data
				? searchCreatorData?.data
				: null;
			// console.log("creatorData", creatorData);

			if (creatorData) {
				setCreator(creatorData);
			} else {
				setCreator(null);
				setSearchUrl('');
				message.error('Creator not found');
			}
		}
		if (isSearchCreatorError) {
			const errMessage = (searchError as any)?.response?.data?.message;
			message.error(`An error occurred: ${errMessage}`);
			console.log('searchError', searchError);
		}
	}, [
		isSearchCreatorSuccess,
		isSearchCreatorError,
		searchUrl,
		searchCreatorData,
	]);

	const handleOk = async () => {
		try {
			await form.validateFields();

			type === 'monitors'
				? addCreatorToMonitor(
						{
							id: modalData?.id,
							creator_mongo_id: creator?._id || null,
							url: form.getFieldValue('link'),
							tags: form.getFieldValue('tags'),
						},
						{
							onSuccess: () => {
								message.success('Added');
								setOpen(false);
								form.resetFields();
								setTags([]);
								setSearchUrl('');
								setCreator(null);
							},
							onError: (error) => {
								const err = error as any;
								message.error(
									`An error occured: ${
										err?.response?.data?.message || err?.message
									}.`,
								);
								console.log('error', error);
							},
						},
				  )
				: addCreatorToCollection(
						{
							collection_id: modalData?.id,
							creator_mongo_id: creator?._id || null,
							url: form.getFieldValue('link'),
							tags: form.getFieldValue('tags'),
						},
						{
							onSuccess: () => {
								message.success('Added');

								setOpen(false);
								form.resetFields();
								setTags([]);
								setSearchUrl('');
								setCreator(null);
							},
							onError: (error) => {
								const err = error as any;
								message.error(
									`An error occured: ${
										err?.response?.data?.message || err?.message
									}.`,
								);
								console.log('error', error);
							},
						},
				  );
		} catch (error) {
			console.log('error', error);
		}
	};

	const handleCancel = () => {
		setOpen(false);
		setTags([]);
		setSearchUrl('');
		setCreator(null);
		form.resetFields();
	};

	const handleChange = (value: string[]) => {
		setTags(value.map((item) => ({ label: item, value: item })));
		selectRef.current?.blur();
	};

	const handleLookupClick = () => {
		if (inputValue?.trim()?.length > 0 && validateCreatorUrl(inputValue)) {
			setSearchUrl(inputValue.trim());
		} else {
			message.error('Please provide creator link');
		}
	};

	const tagsItems = tagsData?.data?.data?.items?.map((item: any) => ({
		label: item?.tag,
		value: item?.tag,
	}));

	return (
		<Modal
			title={'Add creator'}
			centered
			open={isOpen}
			onOk={handleOk}
			onCancel={handleCancel}
			closable
			maskClosable={false}
			footer={[
				<Button
					key="back"
					onClick={handleCancel}
					disabled={isAddToMonitorLoading || isAddToCollectionLoading}
				>
					Cancel
				</Button>,
				<Button
					key="submit"
					type="primary"
					loading={isAddToMonitorLoading}
					onClick={handleOk}
					disabled={
						isAddToMonitorLoading ||
						isSearchCreatorFetching ||
						isAddToCollectionLoading ||
						!creator?.profileName
					}
				>
					Add
				</Button>,
			]}
		>
			<Form
				layout="vertical"
				form={form}
				initialValues={{
					link: '',
					tags: [],
				}}
				// style={{ marginBottom: "1rem" }}
			>
				<Flex
					vertical
					// gap={16}
				>
					{/* {!searchUrl && !creator && ( */}
					<Form.Item
						label={'Creator link'}
						name="link"
						rules={[
							{
								required: true,
								message: 'Please provide creator link',
							},
						]}
						// style={{ margin: 0 }}
					>
						<Flex gap={8}>
							<Input
								placeholder={'https://www.youtube.com/@...'}
								style={{ resize: 'none' }}
								// onBlur={(e) => onBlurInput(e.target.value)}
								disabled={isSearchCreatorFetching}
								value={inputValue}
								onChange={(e) => setInputValue(e.target.value)}
							/>
							<Button
								type="primary"
								loading={isSearchCreatorFetching}
								onClick={handleLookupClick}
								disabled={
									isSearchCreatorFetching || !inputValue || creator?.profileName
								}
							>
								Lookup
							</Button>
						</Flex>
					</Form.Item>
					{/* )} */}

					{!isSearchCreatorFetching && searchUrl && creator?.profileName && (
						<Flex
							key={creator?.profileName || searchUrl}
							gap={8}
							align="center"
							style={{
								padding: '0.5rem',
								marginBottom: '1rem',
								border: '1px solid #ccc',
								borderRadius: '0.5rem',
							}}
						>
							<Avatar
								size={50}
								src={
									creator?.profileImageURL?.startsWith('http')
										? creator?.profileImageURL
										: prepareImageSrc(creator?.profileImageURL) ||
										  placeholderImage
								}
								icon={<UserOutlined />}
							></Avatar>
							<Typography.Title level={5} style={{ margin: 0 }}>
								{creator?.profileName}
							</Typography.Title>

							{creator?.platformHandles?.[0] && (
								<Flex
									align="center"
									justify="center"
									style={{ marginLeft: '1rem' }}
								>
									<Platforms platform={creator?.platformHandles?.[0]} />
								</Flex>
							)}

							<Tooltip title={'Edit'}>
								<Button
									type="dashed"
									// variant="outlined"
									style={{ marginLeft: 'auto' }}
									onClick={() => {
										setSearchUrl('');
										setCreator(null);
									}}
								>
									<EditOutlined />
								</Button>
							</Tooltip>
						</Flex>
					)}

					{!isSearchCreatorFetching && searchUrl && !creator?.profileName && (
						<Flex
							gap={8}
							align="center"
							justify="center"
							style={{
								padding: '0.5rem',
								marginBottom: '1rem',
								border: '1px solid #ccc',
								borderRadius: '0.5rem',
							}}
						>
							<Typography.Title level={5} style={{ margin: 0 }}>
								Creator not found
							</Typography.Title>
							<Tooltip title="Edit">
								<Button
									type="dashed"
									// variant="outlined"
									// style={{ marginLeft: "auto" }}
									onClick={() => {
										setSearchUrl('');
										setCreator(null);
									}}
								>
									<EditOutlined />
								</Button>
							</Tooltip>
						</Flex>
					)}

					<Form.Item
						label="Search tags"
						name="tags"
						// style={{ margin: 0 }}
					>
						<Select
							mode="tags"
							style={{ width: '100%' }}
							placeholder="Search tags"
							onChange={handleChange}
							options={tagsItems || []}
							disabled={isTagsFetching}
							ref={selectRef}
						/>
					</Form.Item>
				</Flex>
			</Form>
		</Modal>
	);
}
